"use client";

import { bookingOverlayOpen, subscribeBookingOverlay } from "@/lib/booking-overlay-state";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useId, useState, useSyncExternalStore } from "react";
import { ArrowRight, X } from "lucide-react";
import { useBlogLocaleNavigation, useSiteLocale } from "./CcLocaleContext";
import { useSiteChromeLocked } from "./CcSiteChromeLock";
import { CC_LANGUAGE_CHOICE_EVENT, languageFlag, normalizeVisitorCountry, persistLanguageChoice, readLanguageChoice, suggestVisitorLocale } from "@/lib/i18n/language-suggestion";
import { COOKIE_CONSENT_CHANGE_EVENT, readCookieConsent } from "@/lib/cookie-consent";
import { formatCopy } from "@/lib/copy-format";
import { getLanguageSuggestionCopy, getLanguageSuggestionCountry } from "@/lib/language-suggestion-copy";
import { getLanguageSwitchOptions } from "@/lib/i18n/language-switching";
import { blogLocaleFromPathname } from "@/lib/blog/locales";

function subscribeConsent(callback: () => void) {
  window.addEventListener(COOKIE_CONSENT_CHANGE_EVENT, callback);
  window.addEventListener("storage", callback);
  return () => { window.removeEventListener(COOKIE_CONSENT_CHANGE_EVENT, callback); window.removeEventListener("storage", callback); };
}
function subscribeChoice(callback: () => void) {
  window.addEventListener(CC_LANGUAGE_CHOICE_EVENT, callback);
  return () => window.removeEventListener(CC_LANGUAGE_CHOICE_EVENT, callback);
}
const hasConsent = () => readCookieConsent() !== null;
const hasChoice = () => readLanguageChoice() !== null;
const serverSnapshot = () => false;

export function CcLanguageSuggestion() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const siteLocale = useSiteLocale();
  const { navigation } = useBlogLocaleNavigation();
  const locked = useSiteChromeLocked();
  const bookingOpen = useSyncExternalStore(subscribeBookingOverlay, bookingOverlayOpen, () => false);
  const consentReady = useSyncExternalStore(subscribeConsent, hasConsent, serverSnapshot);
  const choiceSaved = useSyncExternalStore(subscribeChoice, hasChoice, serverSnapshot);
  const [signals, setSignals] = useState<{ country: string | null; languages: readonly string[] } | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const titleId = useId();
  const descriptionId = useId();
  const devCountry = process.env.NODE_ENV !== "production" ? searchParams.get("devCountry") : null;
  const internalPage = /^\/(?:dev|api)(?:\/|$)/.test(pathname);

  useEffect(() => {
    if (internalPage || choiceSaved) return;
    const controller = new AbortController();
    const url = `/api/visitor-country${devCountry ? `?country=${encodeURIComponent(devCountry)}` : ""}`;
    fetch(url, { cache: "no-store", signal: controller.signal })
      .then((response) => response.ok ? response.json() : { country: null })
      .then((data) => { if (!controller.signal.aborted) setSignals({ country: normalizeVisitorCountry(data.country), languages: navigator.languages }); })
      .catch(() => { if (!controller.signal.aborted) setSignals({ country: null, languages: navigator.languages }); });
    return () => controller.abort();
  }, [devCountry, internalPage, choiceSaved]);

  const blog = navigation?.pathname === pathname.replace(/\/$/, "") ? navigation : null;
  const currentLocale = blog?.lang ?? siteLocale;
  const options = getLanguageSwitchOptions(pathname, blog);
  const available = options.map((option) => option.locale);
  const target = signals ? suggestVisitorLocale(signals.country, signals.languages, available) : null;
  const waitingForBlog = blogLocaleFromPathname(pathname) !== null && !blog;
  const visible = consentReady && !choiceSaved && !dismissed && !internalPage && !waitingForBlog && !locked && !bookingOpen && target && target !== currentLocale;

  useEffect(() => {
    if (!visible) return;
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") { persistLanguageChoice(currentLocale); setDismissed(true); }
    };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, [visible, currentLocale]);

  if (!visible || !target) return null;
  const copy = getLanguageSuggestionCopy(target);
  const country = getLanguageSuggestionCountry(signals?.country ?? null, target);
  const path = options.find((option) => option.locale === target)?.href;
  if (!path) return null;
  const query = new URLSearchParams(searchParams.toString());
  query.delete("devCountry");
  const href = `${path}${query.size ? `?${query}` : ""}${window.location.hash}`;
  const stay = () => { persistLanguageChoice(currentLocale); setDismissed(true); };

  return <aside className="cc-language-suggestion" role="dialog" aria-modal="false" aria-live="polite" aria-labelledby={titleId} aria-describedby={descriptionId} lang={target} data-cc-cursor-ignore>
    <button className="cc-language-suggestion__close" type="button" aria-label={copy.close} onClick={stay}><X size={18} aria-hidden="true" /></button>
    <span className="cc-language-suggestion__flag" aria-hidden="true">{languageFlag(target)}</span>
    <p className="cc-language-suggestion__eyebrow">{copy.eyebrow}</p>
    <h2 id={titleId}>{country ? formatCopy(copy.title, { country }) : copy.browserTitle}</h2>
    <p id={descriptionId} className="cc-language-suggestion__body">{copy.body}</p>
    <div className="cc-language-suggestion__actions">
      {/* A full locale navigation also refreshes the server-rendered site chrome. */}
      <a className="cc-language-suggestion__switch" href={href} hrefLang={target} onClick={() => persistLanguageChoice(target)}>{copy.switch}<ArrowRight size={17} aria-hidden="true" /></a>
      <button className="cc-language-suggestion__stay" type="button" onClick={stay}>{copy.stay}</button>
    </div>
  </aside>;
}
